import { RegisterComponent } from '@/common/register-component';
import { NylasSchedulerConfigConnector } from '@/connector/nylas-scheduler-config-connector';
import { debug } from '@/utils/utils';
import { AttachInternals, Component, Element, Event, EventEmitter, Host, Listen, Prop, State, Watch, h } from '@stencil/core';
import { NylasSchedulerEditor } from '../nylas-scheduler-editor/nylas-scheduler-editor';
import { Configuration, ThemeConfig } from '@nylas/core';
import i18next from '@/utils/i18n';
import { LANGUAGE_CODE } from '@/common/constants';

/**
 * The `nylas-customize-booking-settings` component is a UI component that displays the booking calendar picker.
 * @part ncbs - The booking calendar picker container
 * @part ncbs__header - The header of the booking calendar picker
 * @part ncbs__settings - The settings container
 * @part ncbs__settings-div - The div inside the settings container that contains the checkbox and tooltip for each setting
 * @part ncbs__additional_guests - The additional guests setting
 * @part ncbs__cancellation_options - The cancellation options setting
 * @part ncbs__rescheduling_options - The rescheduling options setting
 */
@Component({
  tag: 'nylas-customize-booking-settings',
  styleUrl: 'nylas-customize-booking-settings.scss',
  shadow: true,
  formAssociated: true,
})
export class NylasCustomizeBookingSettings {
  @Element() host!: HTMLNylasCustomizeBookingSettingsElement;
  /**
   * @standalone
   * The selected config
   */
  @Prop() selectedConfiguration?: Configuration;
  /**
   * @standalone
   * The default additional guests hidden setting as set in the configuration.
   */
  @Prop() defaultHideAdditionalGuests: boolean = this.selectedConfiguration?.scheduler?.hide_additional_guests ?? false;

  /**
   * @standalone
   * The default hide cancellation options setting as set in the configuration.
   */
  @Prop() defaultHideCancellationOptions: boolean = this.selectedConfiguration?.scheduler?.hide_cancellation_options ?? false;

  /**
   * @standalone
   * The default hide rescheduling options setting as set in the configuration.
   */
  @Prop() defaultHideReschedulingOptions: boolean = this.selectedConfiguration?.scheduler?.hide_rescheduling_options ?? false;

  /**
   * @standalone
   * The name of the calendar picker.
   */
  @Prop() name: string = 'customize-booking-settings';

  /**
   * The selected language.
   */
  @Prop() selectedLanguage?: LANGUAGE_CODE;

  /**
   * This prop is used to determined which settings are visible in the component.
   */
  @Prop() showSettings: string[] = ['allowGuestsToInvite', 'hideCancellationOptions', 'hideReschedulingOptions'];
  /**
   * @standalone
   * The theme configuration.
   */
  @Prop({ attribute: 'theme-config' }) readonly themeConfig?: ThemeConfig;

  /**
   * State to store the allowed additional guests setting.
   */
  @State() allowGuestsToInvite!: boolean;

  /**
   * State to store the hide cancel booking setting.
   */
  @State() hideCancellationOptions!: boolean;

  /**
   * State to store the hide reschedule booking setting.
   */
  @State() hideReschedulingOptions!: boolean;

  /**
   * The element internals.
   */
  @AttachInternals() internals!: ElementInternals;

  /**
   * This event is fired when the booking settings are changed.
   */
  @Event() valueChanged!: EventEmitter<{
    value: string;
    name: string;
    valueChanged?: (event: CustomEvent<{ value: string; name: string }>) => void;
  }>;

  /**
   * When a name prop is passed, stencil does not automatically set the name attribute on the host element.
   * Since this component is form-associated, the name attribute is required for form submission.
   * This is a workaround to ensure that the name attribute is set on the host element.
   */
  @Watch('name')
  elementNameChangedHandler(newValue: string) {
    debug('nylas-customize-booking-settings', 'elementNameChangedHandler', newValue);
    this.host.setAttribute('name', newValue);
  }

  @Watch('selectedConfiguration')
  selectedConfigurationChangedHandler(newValue: Configuration) {
    debug('nylas-customize-booking-settings', 'selectedConfigurationChangedHandler', newValue);
    this.allowGuestsToInvite = !newValue?.scheduler?.hide_additional_guests;
    this.hideCancellationOptions = newValue?.scheduler?.hide_cancellation_options ?? false;
    this.hideReschedulingOptions = newValue?.scheduler?.hide_rescheduling_options ?? false;
    this.handleSettingValues(false);
  }

  @Watch('selectedLanguage')
  selectedLanguageChangedHandler(newVal: LANGUAGE_CODE, oldVal: LANGUAGE_CODE) {
    if (newVal === oldVal) return;
    i18next.changeLanguage(newVal);
  }

  @Watch('themeConfig')
  themeConfigChangedHandler(newVal: ThemeConfig, oldVal: ThemeConfig) {
    if (newVal === oldVal) return;
    this.applyThemeConfig(newVal);
  }

  // Lifecycle methods
  connectedCallback() {
    debug('nylas-customize-booking-settings', 'connectedCallback');
    // Use queueMicrotask to defer theme application until after React has set props.
    // This fixes the timing issue where connectedCallback fires before React sets themeConfig.
    queueMicrotask(() => {
      if (this.themeConfig) {
        this.applyThemeConfig(this.themeConfig);
        this.host.setAttribute('data-themed', '');
      }
    });
  }

  disconnectedCallback() {
    debug('nylas-customize-booking-settings', 'disconnectedCallback');
  }

  componentWillLoad() {
    debug('nylas-customize-booking-settings', 'componentWillLoad');
    // See comment in the @Watch('name') decorator for more information.
    this.host.setAttribute('name', this.name);
    this.allowGuestsToInvite = !this.defaultHideAdditionalGuests;
    this.hideCancellationOptions = this.defaultHideCancellationOptions;
    this.hideReschedulingOptions = this.defaultHideReschedulingOptions;
  }

  componentDidLoad() {
    debug('nylas-customize-booking-settings', 'componentDidLoad');
    if (this.selectedConfiguration) {
      this.selectedConfigurationChangedHandler(this.selectedConfiguration);
    } else {
      // Set the default values if the selected configuration is not available
      this.allowGuestsToInvite = !this.defaultHideAdditionalGuests;
      this.hideCancellationOptions = this.defaultHideCancellationOptions;
      this.hideReschedulingOptions = this.defaultHideReschedulingOptions;
      this.handleSettingValues(false);
    }
  }

  componentWillUpdate() {
    debug('nylas-customize-booking-settings', 'componentWillUpdate');
  }

  componentDidUpdate() {
    debug('nylas-customize-booking-settings', 'componentDidUpdate');
  }

  componentWillRender() {
    debug('nylas-customize-booking-settings', 'componentWillRender');
  }

  componentDidRender() {
    debug('nylas-customize-booking-settings', 'componentDidRender');
  }

  @Listen('nylasFormCheckboxToggled')
  nylasFormCheckboxToggledHandler(event: CustomEvent<{ checked: boolean; name: string }>) {
    debug('nylas-customize-booking-settings', 'nylasFormCheckboxToggledHandler', event.detail);
    const { name, checked } = event.detail;

    if (name === 'hide_additional_guests') {
      this.allowGuestsToInvite = checked;
    } else if (name === 'hide_cancellation_options') {
      this.hideCancellationOptions = checked;
    } else if (name === 'hide_rescheduling_options') {
      this.hideReschedulingOptions = checked;
    }
    this.handleSettingValues();
  }

  handleSettingValues(valueChanged: boolean = true) {
    if (valueChanged) {
      this.valueChanged.emit({
        value: JSON.stringify({
          additionalGuestsHidden: this.showSettings.includes('allowGuestsToInvite') ? !this.allowGuestsToInvite : undefined,
          hideCancellationOptions: this.hideCancellationOptions,
          hideReschedulingOptions: this.hideReschedulingOptions,
        }),
        name: this.name,
      });
    }
    this.isInternalsAvailable &&
      this.internals.setFormValue(
        JSON.stringify({
          additionalGuestsHidden: this.showSettings.includes('allowGuestsToInvite') ? !this.allowGuestsToInvite : undefined,
          hideCancellationOptions: this.hideCancellationOptions,
          hideReschedulingOptions: this.hideReschedulingOptions,
        }),
        this.name,
      );
  }

  /**
   * This method is essentially a workaround to check if the internals are available because
   * the unit tests in stencil do not support the internals.
   * @returns boolean
   */
  get isInternalsAvailable() {
    return this.internals !== undefined && typeof this.internals.setFormValue === 'function';
  }

  applyThemeConfig(themeConfig?: ThemeConfig) {
    if (themeConfig) {
      for (const [key, value] of Object.entries(themeConfig)) {
        this.host.style.setProperty(`${key}`, value);
      }
    }
  }

  @RegisterComponent<NylasCustomizeBookingSettings, NylasSchedulerConfigConnector, Exclude<NylasSchedulerEditor['stores'], undefined>>({
    name: 'nylas-customize-booking-settings',
    stateToProps: new Map([
      ['schedulerConfig.selectedConfiguration', 'selectedConfiguration'],
      ['schedulerConfig.selectedLanguage', 'selectedLanguage'],
      ['schedulerConfig.themeConfig', 'themeConfig'],
    ]),
    fireRegisterEvent: true,
  })
  render() {
    const showComponent = typeof this.hideCancellationOptions === 'boolean' && typeof this.hideReschedulingOptions === 'boolean' && typeof this.allowGuestsToInvite === 'boolean';
    const showHideAdditionalGuests = this.showSettings.includes('allowGuestsToInvite');

    return (
      <Host>
        {showComponent && (
          <div class="nylas-customize-booking-settings" part="ncbs">
            <div class="header" part="ncbs__header">
              <h3>{i18next.t('nylasCustomizeBookingSettings.headerTitle')}</h3>
              <p>{i18next.t('nylasCustomizeBookingSettings.headerSubTitle')}</p>
            </div>

            <div class="nylas-customize-booking-settings__settings" part="ncbs__settings">
              {showHideAdditionalGuests && (
                <div class="nylas-customize-booking-settings__settings-div" part="ncbs__settings-div , ncbs__additional_guests">
                  <checkbox-component
                    name="hide_additional_guests"
                    label={i18next.t('nylasCustomizeBookingSettings.allowGuests.label')}
                    checked={this.allowGuestsToInvite}
                    themeConfig={this.themeConfig}
                  >
                    <span slot="label" class="label">
                      <p class="label-contents">
                        {i18next.t('nylasCustomizeBookingSettings.allowGuests.label', { interpolation: { escapeValue: false, escape: s => `${s}`, useRawValueToEscape: true } })}
                      </p>
                    </span>
                  </checkbox-component>
                </div>
              )}
              <div class="nylas-customize-booking-settings__settings-div" part="ncbs__settings-div , ncbs__rescheduling_options">
                <checkbox-component
                  name="hide_rescheduling_options"
                  label="Hide 'Reschedule' option on booking page and emails."
                  checked={this.hideReschedulingOptions}
                  themeConfig={this.themeConfig}
                >
                  <span slot="label" class="label">
                    <p class="label-contents">{i18next.t('nylasCustomizeBookingSettings.hideReschedule.label')}</p>
                    <tooltip-component id="reschedule">
                      <info-icon slot="tooltip-icon" />
                      <span slot="tooltip-content">{i18next.t('nylasCustomizeBookingSettings.hideReschedule.tooltip.desc')}</span>
                    </tooltip-component>
                  </span>
                </checkbox-component>
              </div>
              <div class="nylas-customize-booking-settings__settings-div" part="ncbs__settings-div , ncbs__cancellation_options">
                <checkbox-component
                  name="hide_cancellation_options"
                  label="Hide 'Cancel' option on booking page and emails."
                  checked={this.hideCancellationOptions}
                  themeConfig={this.themeConfig}
                >
                  <span slot="label" class="label">
                    <p class="label-contents">{i18next.t('nylasCustomizeBookingSettings.hideCancel.label')}</p>
                    <tooltip-component id="cancel">
                      <info-icon slot="tooltip-icon" />
                      <span slot="tooltip-content">{i18next.t('nylasCustomizeBookingSettings.hideCancel.tooltip.desc')}</span>
                    </tooltip-component>
                  </span>
                </checkbox-component>
              </div>
            </div>
          </div>
        )}
      </Host>
    );
  }
}
